import axios from 'axios';
import { ListProjectImagesApi } from './api';

const memoryCache = new Map();
const storageKey = (category) => `classic-insides:projects:${category}`;

const normaliseCategory = (category) =>
  String(category || 'residential').trim().toLowerCase();

export const getCachedProjects = (category) => {
  const normalisedCategory = normaliseCategory(category);

  if (memoryCache.has(normalisedCategory)) {
    return memoryCache.get(normalisedCategory);
  }

  try {
    const cached = window.sessionStorage.getItem(storageKey(normalisedCategory));
    if (!cached) return null;

    const projects = JSON.parse(cached);
    if (!Array.isArray(projects)) return null;

    memoryCache.set(normalisedCategory, projects);
    return projects;
  } catch {
    return null;
  }
};

const cacheProjects = (category, projects) => {
  memoryCache.set(category, projects);

  try {
    window.sessionStorage.setItem(storageKey(category), JSON.stringify(projects));
  } catch {
    // The in-memory cache still protects the current visit if storage is blocked.
  }
};

const wait = (duration, signal) =>
  new Promise((resolve, reject) => {
    const timer = window.setTimeout(resolve, duration);

    signal?.addEventListener('abort', () => {
      window.clearTimeout(timer);
      reject(new DOMException('Request aborted', 'AbortError'));
    }, { once: true });
  });

export const fetchProjects = async (category, { signal, retries = 2 } = {}) => {
  const normalisedCategory = normaliseCategory(category);
  let lastError;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      const response = await axios.get(ListProjectImagesApi, {
        params: { category: normalisedCategory },
        signal,
        timeout: 15000,
      });
      const appData = response?.data?.app_data;

      if (appData?.StatusCode !== 6000 || !Array.isArray(appData?.data)) {
        throw new Error('The projects API returned an invalid response.');
      }

      const projects = appData.data
        .filter((project) => {
          const projectCategory = normaliseCategory(project?.project_category);
          return projectCategory === normalisedCategory;
        })
        .reverse();

      cacheProjects(normalisedCategory, projects);
      return projects;
    } catch (error) {
      if (signal?.aborted || axios.isCancel(error) || error?.name === 'CanceledError') {
        throw error;
      }

      lastError = error;
      if (attempt < retries) {
        await wait(500 * (attempt + 1), signal);
      }
    }
  }

  throw lastError;
};
